import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/feature/Pagination";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  DEFAULT_FILTERS,
  PAGE_SIZE,
  applyFilters,
  getCategory,
  getCategoryCount,
  getCategoryVideos,
  getTotalPages,
  paginate,
  type FilterState,
} from "@/utils/video";
import { categories } from "@/mocks/categories";

export default function CategoryPage() {
  const { categoryName } = useParams<{ categoryName: string }>();
  const category = getCategory(categoryName);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: category
      ? `${category.name}视频 - 亚洲激情视频频道`
      : "分类不存在 - 亚洲激情视频",
    description: category
      ? `${category.description.slice(0, 70)} 亚洲激情视频${category.name}频道，支持类型、时长与发布日期筛选。`
      : "该分类不存在或已调整，返回亚洲激情视频首页继续浏览其他内容。",
    keywords: category
      ? `${category.name},亚洲激情视频,${category.name}视频,视频分类,亚洲视频`
      : "亚洲激情视频,视频分类",
  });

  const list = useMemo(
    () => applyFilters(getCategoryVideos(category?.slug ?? ""), filters),
    [category?.slug, filters],
  );

  const totalPages = getTotalPages(list.length, PAGE_SIZE);
  const safePage = Math.min(page, totalPages);
  const pageItems = paginate(list, safePage, PAGE_SIZE);

  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  if (!category) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1440px] px-4 py-20 md:px-8">
          <EmptyState
            icon="ri-folder-unknow-line"
            title="该分类不存在"
            description="你访问的分类可能已经调整或不存在，返回首页继续浏览其他内容。"
          />
          <div className="mt-6 flex justify-center">
            <Link
              to="/"
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-500"
            >
              <i className="ri-home-4-line text-sm" />
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="border-b border-background-800 bg-gradient-to-b from-primary-950/50 to-background-950">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "分类", to: "/category/night" },
              { label: category.name },
            ]}
          />

          <div className="mt-5 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-900/70 text-primary-200">
                  <i className={`${category.icon} text-xl`} />
                </span>
                <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
                  {category.name}视频
                </h1>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground-400">
                {category.description}
              </p>
            </div>

            <div className="flex shrink-0 gap-6">
              <div>
                <p className="font-heading text-2xl font-bold text-foreground-50">
                  {getCategoryCount(category.slug)}
                </p>
                <p className="mt-1 text-[11px] text-foreground-500">分类片目</p>
              </div>
              <div className="h-10 w-px bg-background-800" />
              <div>
                <p className="font-heading text-2xl font-bold text-foreground-50">{safePage}</p>
                <p className="mt-1 text-[11px] text-foreground-500">当前页 / 共 {totalPages} 页</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        <FilterBar
          filters={filters}
          onChange={handleFilterChange}
          onReset={handleReset}
          resultCount={list.length}
        />

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-600">其他分类：</span>
          {categories
            .filter((item) => item.slug !== category.slug)
            .map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className="rounded-full border border-background-800 px-3 py-1 text-xs text-foreground-400 transition-colors hover:border-primary-700 hover:text-primary-200"
              >
                {item.name}
              </Link>
            ))}
        </div>

        <div className="mt-6">
          {pageItems.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="没有符合条件的视频"
              description="当前筛选组合下暂无内容，试试放宽时长或日期范围，或者直接重置全部筛选条件。"
              actionText="重置筛选"
              onAction={handleReset}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pageItems.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}
        </div>

        <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
      </section>
    </SiteLayout>
  );
}